/**
 * RemindersTab - the dashboard's data reminder schedule.
 *
 * Shown only on a dashboard (a root node): a card inherits the dashboard's
 * schedule rather than carrying its own. Everyone who can enter data on a card
 * is reminded automatically, so the only list to maintain here is the extra
 * people who should be told without holding that access.
 *
 * Layout follows the Assurance Rules tab: a numbered rail for when
 * reminders go out and who they go to.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { InfoIcon, UsersThreeIcon } from '@phosphor-icons/react';
import { toast } from 'src/components/ui/toast';
import Banner from 'src/components/ui/Banner';
import Button from 'src/components/ui/Button';
import Combobox from 'src/components/ui/Combobox';
import { InputField } from 'src/components/ui/Input';
import Loading from 'src/components/ui/Loading';
import { ToggleSetting } from 'src/components/ui/ToggleSetting';
import Tooltip from 'src/components/ui/Tooltip';
import UserAvatar from '@/components/ui/UserAvatar';
import { usePostHog } from 'src/lib/posthog';
import { ResourcePermissionRole } from 'src/features/chat/types';
import {
  useDashboardReminders,
  useNodePermissions,
  useSetDashboardReminderMute,
  useUpdateDashboardReminders
} from '../api/hooks';
import type { ReminderEditor } from './reminderEditor';
import RuleStep from './RuleStep';
import { formatLongDate } from 'src/lib/utils/formatDate';

const MAX_DUE_OFFSET_DAYS = 60;

/** Same labels as the share modal — anyone at Data Entry or above is reminded automatically. */
const ROLE_LABEL: Record<number, string> = {
  [ResourcePermissionRole.READ]: 'View',
  [ResourcePermissionRole.COMMENT]: 'Comment',
  [ResourcePermissionRole.DATA_ENTRY]: 'Data Entry',
  [ResourcePermissionRole.WRITE]: 'Edit',
  [ResourcePermissionRole.SHARE]: 'Share',
  [ResourcePermissionRole.OWNER]: 'Owner'
};

function isAutomaticReminderRole(permission: number): boolean {
  return permission >= ResourcePermissionRole.DATA_ENTRY;
}

function personLabel(user: { first_name: string | null; last_name: string | null; email: string }): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name || user.email;
}

function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

interface RemindersTabProps {
  dashboardId: string;
  /** Only a dashboard owner may change the schedule; everyone else reads it. */
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onRegisterEditor?: (editor: ReminderEditor | null) => void;
}

export default function RemindersTab({ dashboardId, canEdit, onDirtyChange, onRegisterEditor }: RemindersTabProps) {
  const posthog = usePostHog();
  const { data: settings, isLoading } = useDashboardReminders(dashboardId);
  const { data: permissions } = useNodePermissions(dashboardId);
  const updateReminders = useUpdateDashboardReminders(dashboardId);
  const setMute = useSetDashboardReminderMute(dashboardId);

  const [enabled, setEnabled] = useState(false);
  const [dueOffset, setDueOffset] = useState(10);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);

  const viewedRef = useRef(false);
  const skipNextHydration = useRef(false);

  // Seed the form from the server once it arrives, and again after a save.
  // Mute refreshes the same query; skip that pass so unsaved schedule edits stay.
  useEffect(() => {
    if (!settings) return;
    if (skipNextHydration.current) {
      skipNextHydration.current = false;
      return;
    }
    setEnabled(settings.enabled);
    setDueOffset(settings.data_due_offset_days);
    setRecipientIds(settings.recipients.map((recipient) => recipient.user_id));
  }, [settings]);

  useEffect(() => {
    if (!settings || viewedRef.current) return;
    viewedRef.current = true;
    posthog?.capture('dashboard_reminders_tab_viewed', {
      dashboard_id: dashboardId,
      can_edit: canEdit,
      enabled: settings.enabled
    });
  }, [settings, dashboardId, canEdit, posthog]);

  const people = useMemo(() => permissions?.shared_with ?? [], [permissions]);
  const automaticPeople = useMemo(
    () =>
      people
        .filter((person) => isAutomaticReminderRole(person.permission))
        .slice()
        .sort((a, b) => personLabel(a).localeCompare(personLabel(b))),
    [people]
  );
  const automaticGroups = useMemo(
    () =>
      (permissions?.group_shares ?? [])
        .filter((group) => isAutomaticReminderRole(group.permission))
        .slice()
        .sort((a, b) => a.group_name.localeCompare(b.group_name)),
    [permissions?.group_shares]
  );

  const dueOffsetValid = Number.isInteger(dueOffset) && dueOffset >= 0 && dueOffset <= MAX_DUE_OFFSET_DAYS;
  const canSave = canEdit && dueOffsetValid;
  const savedRecipientIds = (settings?.recipients ?? []).map((recipient) => recipient.user_id);
  const dirty =
    !!settings &&
    (enabled !== settings.enabled ||
      dueOffset !== settings.data_due_offset_days ||
      [...recipientIds].sort((a, b) => a - b).join(',') !== [...savedRecipientIds].sort((a, b) => a - b).join(','));

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const extraOptions = useMemo(
    () =>
      people
        .filter((person) => !isAutomaticReminderRole(person.permission) || recipientIds.includes(person.user_id))
        .map((person) => ({
          id: person.user_id,
          label: personLabel(person),
          value: person.email
        })),
    [people, recipientIds]
  );
  const extraEmails = useMemo(() => {
    const byId = new Map(people.map((person) => [person.user_id, person.email]));
    return recipientIds.flatMap((id) => {
      const email = byId.get(id);
      return email ? [email] : [];
    });
  }, [people, recipientIds]);

  const handleMute = async (muted: boolean) => {
    skipNextHydration.current = true;
    try {
      await setMute.mutateAsync(muted);
      toast.success(
        muted ? 'Your reminders for this dashboard are muted.' : "You'll get reminders for this dashboard again."
      );
      posthog?.capture(muted ? 'dashboard_reminders_muted' : 'dashboard_reminders_unmuted', {
        dashboard_id: dashboardId
      });
    } catch {
      skipNextHydration.current = false;
      toast.error('Could not update your reminder preference');
    }
  };

  useEffect(() => {
    if (!onRegisterEditor) return;
    onRegisterEditor({
      validate: () => {
        if (!canSave) {
          toast.error(`The deadline must be between 0 and ${MAX_DUE_OFFSET_DAYS} days after the period ends.`);
          return false;
        }
        return true;
      },
      save: async () => {
        await updateReminders.mutateAsync({
          enabled,
          data_due_offset_days: dueOffset,
          recipient_user_ids: recipientIds
        });
        posthog?.capture('dashboard_reminders_saved', {
          dashboard_id: dashboardId,
          enabled,
          data_due_offset_days: dueOffset,
          extra_recipient_count: recipientIds.length
        });
      }
    });
    return () => onRegisterEditor(null);
  }, [onRegisterEditor, canSave, updateReminders, enabled, dueOffset, recipientIds, posthog, dashboardId]);

  if (isLoading || !settings) {
    return <Loading />;
  }

  const preview = settings.preview;
  const previewLine =
    preview.remindable_card_count === 0
      ? 'No cards hold their own data yet, so there is nothing to remind about.'
      : settings.enabled
        ? `Next reminder ${formatLongDate(preview.next_run, { empty: 'No upcoming reminder' })} · ${countLabel(preview.recipient_count, 'person', 'people')} · ${countLabel(preview.remindable_card_count, 'metric', 'metrics')}`
        : 'Save to see when the next reminder goes out.';

  return (
    <div className="space-y-5">
      {!canEdit && (
        <Banner variant="info">
          <span className="text-sm">
            <strong>View only</strong> — you need owner permission to change these settings. You can still mute
            reminders for yourself below.
          </span>
        </Banner>
      )}

      <ToggleSetting
        checked={enabled}
        onChange={setEnabled}
        disabled={!canEdit}
        title="Send reminders for this dashboard"
        description="If data is still missing, people get a notification in the morning on the day it's due."
      />

      {enabled && (
        <div className="space-y-4">
          <p className="text-xs text-content-subtle -mt-3">
            {previewLine}
            {dirty && settings.enabled ? ' · unsaved changes' : null}
          </p>

          <ol className="flex flex-col [word-break:normal]">
            <RuleStep
              number={1}
              title="When"
              info="Each card uses its own cadence. A monthly card's September data is due this many days after 30 September; a quarterly card's after the quarter closes. People get a notification in the morning on that day, if the data is still missing."
              isLast={false}
            >
              <div className="flex items-center gap-2">
                <InputField
                  id="reminder-due-offset"
                  type="number"
                  min={0}
                  max={MAX_DUE_OFFSET_DAYS}
                  step={1}
                  value={String(dueOffset)}
                  disabled={!canEdit}
                  state={dueOffsetValid ? 'default' : 'error'}
                  onChange={(event) => setDueOffset(Number(event.target.value))}
                  wrapperClassName="w-24"
                  aria-label="Data is due this many days after the period ends"
                />
                <span className="text-sm text-content-subtle">days after the period ends</span>
              </div>
            </RuleStep>

            <RuleStep
              number={2}
              title="Who gets a reminder"
              info="Anyone with Data Entry, Edit, Share, or Owner on a card that still needs data. View and Comment are not enough."
              isLast
            >
              <div className="space-y-3">
                {automaticPeople.length === 0 && automaticGroups.length === 0 ? (
                  <p className="text-xs text-content-subtle">
                    Nobody on this dashboard has Data Entry or higher yet. Share it to add people here.
                  </p>
                ) : (
                  <ul className="rounded-lg border border-border-default divide-y divide-border-subtle max-h-56 overflow-y-auto">
                    {automaticPeople.map((person) => {
                      const name = personLabel(person);
                      return (
                        <li key={person.user_id} className="flex items-center gap-3 px-3 py-2">
                          <UserAvatar
                            firstname={person.first_name}
                            surname={person.last_name}
                            email={person.email}
                            size="sm"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-content-default truncate">{name}</p>
                            {name !== person.email && (
                              <p className="text-xs text-content-subtle truncate">{person.email}</p>
                            )}
                          </div>
                          <span className="shrink-0 text-xs text-content-subtle">
                            {ROLE_LABEL[person.permission] ?? 'Data Entry'}
                          </span>
                        </li>
                      );
                    })}
                    {automaticGroups.map((group) => (
                      <li key={group.group_id} className="flex items-center gap-3 px-3 py-2">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-content-subtle">
                          <UsersThreeIcon size={14} weight="bold" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-content-default truncate">{group.group_name}</p>
                          <p className="text-xs text-content-subtle">Group</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm font-medium text-content-default">Also notify</span>
                    <Tooltip
                      title="Only people already shared with View or Comment. Data Entry and above already get a reminder. They are only told about cards they can see. Sharing with the whole organisation does not add anyone."
                      arrow
                      placement="right"
                    >
                      <span className="inline-flex cursor-help text-content-subtlest" aria-label="Who can be added">
                        <InfoIcon size={14} weight="bold" />
                      </span>
                    </Tooltip>
                  </div>
                  <Combobox
                    multiple
                    size="sm"
                    disabled={!canEdit}
                    placeholder="Add people…"
                    ariaLabel="Also notify"
                    searchPlaceholder="Search people with View or Comment…"
                    emptyText="Share them with View or Comment on this dashboard first."
                    options={extraOptions}
                    value={extraEmails}
                    onChange={(emails) => {
                      const byEmail = new Map(people.map((person) => [person.email, person.user_id]));
                      setRecipientIds(
                        emails.flatMap((email) => {
                          const userId = byEmail.get(email);
                          return userId == null ? [] : [userId];
                        })
                      );
                    }}
                  />
                </div>
              </div>
            </RuleStep>
          </ol>

          <div className="border-t border-border-default pt-4">
            <Button
              variant="outline"
              size="sm"
              loading={setMute.isPending}
              onClick={() => void handleMute(!settings.muted)}
            >
              {settings.muted ? 'Unmute my reminders' : 'Mute my reminders'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
